// Generic typed async hook. Internal building block for useGhostPosts /
// useYoutubeUploads. Handles loading, error, and abort-on-unmount.

import { useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export type AsyncFetcher<T> = (signal: AbortSignal) => Promise<T>;

export function useAsync<T>(fetcher: AsyncFetcher<T>, deps: ReadonlyArray<unknown>): AsyncState<T> {
  // Track the latest fetcher in a ref so we don't need to add it to the
  // dependency array (callers pass it inline; the meaningful deps are in `deps`).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    fetcherRef
      .current(ctrl.signal)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setState({ data: null, loading: false, error: err instanceof Error ? err : new Error(String(err)) });
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
